<div class="service-details flex flex-col gap-8" *ngIf="viewState$ | async as viewState">
  <div *ngIf="viewState.status === 'loading'" class="service-skeleton flex flex-col gap-8" aria-busy="true">
    <section class="service-header flex flex-col justify-between gap-4 rounded-2xl p-24 md:flex-row md:items-center">
      <div class="service-identity flex items-center gap-3">
        <ngx-skeleton-loader class="block skeleton-icon"></ngx-skeleton-loader>
        <div>
          <ngx-skeleton-loader class="block skeleton-title"></ngx-skeleton-loader>
          <ngx-skeleton-loader class="block skeleton-subtitle"></ngx-skeleton-loader>
        </div>
      </div>
    </section>
    <section class="pipeline-header flex flex-wrap items-center justify-between gap-2">
      <ngx-skeleton-loader class="block skeleton-heading"></ngx-skeleton-loader>
    </section>
    <section class="pipeline relative flex flex-col gap-6 pl-8">
      <div class="pipeline-step relative flex items-start gap-4" *ngFor="let step of [1, 2, 3]">
        <ngx-skeleton-loader class="block skeleton-step"></ngx-skeleton-loader>
        <div class="stage-card skeleton-card grow flex flex-wrap items-center justify-between gap-4 p-20 rounded-2xl">
          <div>
            <ngx-skeleton-loader class="block skeleton-stage-title"></ngx-skeleton-loader>
            <ngx-skeleton-loader class="block skeleton-stage-subtitle"></ngx-skeleton-loader>
          </div>
          <ngx-skeleton-loader class="block skeleton-button"></ngx-skeleton-loader>
        </div>
      </div>
    </section>
  </div>

  <div *ngIf="viewState.status === 'error'" class="state-message state-error p-32 text-center rounded-2xl">
    {{ 'FEATURES.SERVICE_DEFINITION_DETAILS.LOAD_ERROR' | transloco }}
  </div>
  <ng-container *ngIf="viewState.status === 'ready' && viewState.service as service">
    <section class="service-header flex flex-col justify-between gap-4 rounded-2xl p-24 md:flex-row md:items-center">
      <div class="service-identity flex items-center gap-3">
        <div class="service-icon flex shrink-0 items-center justify-center w-48 h-48 rounded-2xl" role="img" [attr.aria-label]="service.name">
          <mat-icon svgIcon="building_columns" class="service-icon-glyph w-24 h-24 mr-0"></mat-icon>
        </div>
        <div>
          <h2 class="service-title m-0 font-bold">{{ service.name }}</h2>
          <p class="service-description mt-4 mb-0">{{ service.solutionName }}</p>
        </div>
      </div>
    </section>
    <div *ngIf="!service.hasInstances" class="state-message no-records flex flex-col items-center justify-center p-32 text-center rounded-2xl">
      <img src="/assets/images/no_result.svg" alt="{{ 'FEATURES.SERVICE_DEFINITION_DETAILS.NO_DATA_IMAGE_ALT' | transloco }}" />
      <div class="no-records-text mt-16 font-medium">{{ 'FEATURES.SERVICE_DEFINITION_DETAILS.NO_RECORDS_FOUND' | transloco }}</div>
    </div>
    <ng-container *ngIf="service.hasInstances && service.developmentStages.length">
      <section class="pipeline-header flex flex-wrap items-center justify-between gap-2">
        <h3 class="pipeline-title m-0 font-bold">
          <mat-icon svgIcon="bars_staggered"></mat-icon>
          {{ 'FEATURES.SERVICE_DEFINITION_DETAILS.DEVELOPMENT_HEADING' | transloco }}
        </h3>
      </section>
      <section class="pipeline relative flex flex-col gap-6 pl-8">
        <div class="pipeline-step relative flex items-start gap-4" *ngFor="let stage of service.developmentStages; let i = index; let last = last">
          <div class="pipeline-connector absolute left-[22px] top-12 -bottom-6 w-4 z-0" *ngIf="!last"></div>
          <div class="step-badge relative z-[1] flex shrink-0 items-center justify-center w-48 h-48 rounded-2xl font-extrabold">{{ i + 1 | number: '2.0-0' }}</div>
          <ng-container *ngTemplateOutlet="stageCard; context: { $implicit: stage }"></ng-container>
        </div>
      </section>
    </ng-container>
    <ng-container *ngIf="service.hasInstances && service.pathToLiveStages.length">
      <section class="pipeline-header flex flex-wrap items-center justify-between gap-2">
        <h3 class="pipeline-title m-0 font-bold">
          <mat-icon svgIcon="bars_staggered"></mat-icon>
          {{ 'FEATURES.SERVICE_DEFINITION_DETAILS.PATH_TO_LIVE_HEADING' | transloco }}
        </h3>
      </section>
      <section class="pipeline relative flex flex-col gap-6 pl-8">
        <div class="pipeline-step relative flex items-start gap-4" *ngFor="let stage of service.pathToLiveStages; let i = index; let last = last">
          <div class="pipeline-connector absolute left-[22px] top-12 -bottom-6 w-4 z-0" *ngIf="!last"></div>
          <div class="step-badge relative z-[1] flex shrink-0 items-center justify-center w-48 h-48 rounded-2xl font-extrabold">{{ service.developmentStages.length + i + 1 | number: '2.0-0' }}</div>
          <ng-container *ngTemplateOutlet="stageCard; context: { $implicit: stage }"></ng-container>
        </div>
      </section>
    </ng-container>
    <ng-template #stageCard let-stage>
      <div class="stage-card grow flex flex-wrap items-center justify-between gap-4 p-20 rounded-2xl" [class.cursor-pointer]="stage.hasInstance" (click)="stage.hasInstance && navigateToEnvironment(stage)">
        <div>
          <div class="stage-title flex flex-wrap items-center gap-2">
            <span class="stage-name font-extrabold">
              {{ stage.environment }}
            </span>
            <span class="badge badge-class">
              {{ 'FEATURES.SERVICE_DEFINITION_DETAILS.ENVIRONMENT_CLASS' | transloco: { deploymentClass: stage.deploymentClass } }}
            </span>
            <span *ngIf="!stage.hasInstance" class="badge badge-class">
              {{ 'FEATURES.SERVICE_DEFINITION_DETAILS.NOT_AVAILABLE' | transloco }}
            </span>
            <span *ngIf="stage.hasInstance && lifecycleStatus[stage.lifecycleState] as info" class="stage-status flex items-center ml-4" [ngClass]="info.statusClass">
              <span class="status-dot w-8 h-8" [ngClass]="info.colorClass"></span>
              <span class="status-label mat-subtitle-2 fw-600 ml-8">{{ info.label | transloco }}</span>
            </span>
          </div>
          <div class="stage-subtitle flex items-center gap-3 mt-4">
            <span>{{ 'FEATURES.SERVICE_DEFINITION_DETAILS.SOLUTION_VERSION' | transloco: { version: stage.version || '-' } }}</span>
            <span class="vertical-line"></span>
            <span>{{ 'FEATURES.SERVICE_DEFINITION_DETAILS.RELEASE_PACKAGE' | transloco: { version: stage.releasePackage || '-' } }}</span>
          </div>
        </div>
        <button *ngIf="stage.hasInstance" class="btn-inspect inline-flex items-center justify-center whitespace-nowrap">
          <mat-icon svgIcon="sliders"></mat-icon>
          {{ 'BLUEPRINT_SUMMARY.VIEW_DETAILS' | transloco }}
        </button>
      </div>
    </ng-template>
  </ng-container>
</div>
